/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Grid,
  IconButton,
  Button,
  Box,
  CircularProgress
} from '@material-ui/core'
import { CloudDownload, Delete, Visibility } from '@material-ui/icons'
import { ServerError } from '../../../schemas/Error'
import PageContainer from '../Common/PageContainer'
import ResponsiveList, { ResponsiveColumn } from '../Common/ResponsiveList'
import { urls } from '../../../urls'
import ElectronicBillService from '../../../services/ElectronicBill'

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  loadMoreButton: {
    minHeight: 44,
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  loadingContainer: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: '0',
    right: '0',
    background: '#BFBFBFBF',
    opacity: 0.6,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '50vh'
  }
}))

// - in this part all the component texts are housed
const texts = {
  header: {
    description: 'Lista de programaciones'
  },
  body: {
    buttonLoadMore: {
      label: 'Cargar más ...'
    }
  }
}

// widthPct: porcentaje del ancho de la tabla (las columnas visibles siempre suman 100%)
const columns: ResponsiveColumn[] = [
  {
    field: 'name',
    headerName: 'Nombre',
    widthPct: 30,
    editable: false,
    cardTitle: true,
  },
  {
    field: 'idForm',
    headerName: 'Factura base',
    widthPct: 18,
    editable: false,
  },
  {
    field: 'startDate',
    headerName: 'Inicio',
    editable: false,
    widthPct: 16,
  },
  {
    field: 'endDate',
    headerName: 'Fin',
    editable: false,
    widthPct: 16,
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    widthPct: 20,
    minWidth: 100,
    type: 'actions',
    renderCell: (params: any) => (
      <ActionButtons params={params} />
    )
  }
]

// ------------ Init state -----------
interface State {
  loadMoreButtonDisabled: boolean
  lastEvaluatedKey?: string
  schedules: any[]
  loading: boolean
}

const initState: State = {
  loadMoreButtonDisabled: false,
  schedules: [],
  loading: true
}

function ActionButtons(props: any) {
  const navigate = useNavigate()

  const onView = () => {
    navigate(`/${urls.app.index}/${urls.app.main.electronicBill.form}?number=${props.params.row.idForm ?? ''}`)
  }

  const onDelete = async () => {
    try {
      const electronicBillService= new ElectronicBillService()
      await electronicBillService.deleteSchedule(props.params.row.code)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError && serverError.response) {
          return toast.error(serverError.response.data.message)
        }
      }
    }
  }

  return (
    <React.Fragment>
      <form onSubmit={() => onDelete()}>
        <IconButton
          onClick={() => onView()}
        >
          <Visibility fontSize={"small"} color="primary" />
        </IconButton>
    
        <IconButton
          type="submit"
        >
          <Delete fontSize={"small"} color="error" />
        </IconButton>
      </form>
    </React.Fragment>
  )
}

export default function ScheduledBillList() {

  const classes = useStyles()

  const [state, setState] = useState<State>(initState)

  React.useEffect(() => {
    async function loadData() {
      const electronicBillService= new ElectronicBillService()
      const response = await electronicBillService.getSchedules({ limit: 10 })
      setState({
        ...state,
        schedules: response.schedules,
        lastEvaluatedKey: response.lastEvaluatedKey,
        loadMoreButtonDisabled: response.lastEvaluatedKey !== undefined ? false : true,
        loading: false
      })
    }
    loadData()
  }, [])

  const onLoadMore = async () => {
    try {
      const electronicBillService= new ElectronicBillService()
      const response = await electronicBillService.getSchedules({ limit: 10, lastEvaluatedKey: state.lastEvaluatedKey })
      const newSchedules = response.schedules
      setState({
        ...state,
        schedules: state.schedules.concat(newSchedules),
        lastEvaluatedKey: response.lastEvaluatedKey,
        loadMoreButtonDisabled: response.lastEvaluatedKey !== undefined ? false : true
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError && serverError.response) {
          return toast.error(serverError.response.data.message)
        }
      }
    }
  }

  return (
    <PageContainer title={texts.header.description}>
      <Grid container spacing={3}>

        {/* ----- Listado: tabla (>= 600px) o cards (mobile) ------*/}
        <Grid item xs={12}>
          <ResponsiveList
            rows={state.schedules}
            columns={columns}
            keyId='code'
            cardActions={(row) => <ActionButtons params={{ row }} />}
          />
        </Grid>

        <Grid item xs={12} className={classes.alignRight}>
          <Button
            type="button"
            color="primary"
            className={classes.loadMoreButton}
            startIcon={<CloudDownload />}
            disabled={state.loadMoreButtonDisabled}
            onClick={() => onLoadMore()}
          >
            {texts.body.buttonLoadMore.label}
          </Button>
        </Grid>
      </Grid>
      {
        state.loading ?
        (
          <div className={classes.loadingContainer}>
            <Box>
              <CircularProgress/>
            </Box>
          </div>
        ) : ''
      }
    </PageContainer>
  )
}
